import React from 'react';
import { motion } from 'framer-motion';
import { buttons, motion as motionTokens, radius, shadows } from '../../styles/design-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * UnifiedButton Component
 * Standard button component with proper variant hierarchy and Framer Motion animations
 * 
 * @param {string} variant - Button variant: 'default' | 'secondary' | 'ghost' | 'outline' | 'link' | 'destructive'
 * @param {string} size - Button size: 'small' | 'default' | 'large' | 'icon'
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Button content
 * @param {function} onClick - Click handler
 */
const UnifiedButton = ({ 
  variant = 'default', 
  size = 'default',
  disabled = false,
  className = '', 
  children,
  onClick = null,
  type = 'button',
  ...props 
}) => {
  const prefersReducedMotion = useReducedMotion();
  
  // Base button styles
  const baseClass = `inline-flex items-center justify-center font-semibold ${radius?.button} focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`;
  
  // Size classes from design tokens
  const sizeClass = buttons?.sizes?.[size] || buttons?.sizes?.default;
  
  // Variant classes with shadow for primary and secondary buttons
  const variantClasses = {
    default: `bg-primary text-primary-foreground ${shadows?.md}`,
    secondary: `bg-secondary text-secondary-foreground ${shadows?.md}`,
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
    destructive: `bg-destructive text-destructive-foreground ${shadows?.md}`,
  };
  
  const variantClass = variantClasses?.[variant] || variantClasses?.default;
  
  // Combine all classes
  const combinedClass = `${baseClass} ${sizeClass} ${variantClass} ${className}`?.trim();
  
  // Animation variants based on motion preference
  const hoverAnimation = prefersReducedMotion 
    ? {} 
    : (variant === 'ghost' || variant === 'link' ? motionTokens?.hover?.scaleSubtle : motionTokens?.hover?.scale);
  
  const tapAnimation = prefersReducedMotion 
    ? {} 
    : motionTokens?.tap?.scaleSubtle;

  return (
    <motion.button 
      type={type}
      className={combinedClass}
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? {} : hoverAnimation}
      whileTap={disabled ? {} : tapAnimation}
      transition={{ duration: motionTokens?.duration?.fast, ease: motionTokens?.easing?.default }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export default UnifiedButton;